import { defineStore } from "pinia";
import { ref } from "vue";
import { fetchCurrentUserInfo } from "@app/api/user";
import { useAuthStore } from "@shared/store/modules/auth";
import type { CurrentUserInfoData, PermissionAction, UserDetail, UserMenuItem } from "@shared/types/user";

/** 管理端当前用户信息（会话内缓存；menus 由路由守卫拉 info 时同步水合） */
export const useCurrentUserStore = defineStore("currentUser", () => {
    const userDetail = ref<UserDetail | null>(null);
    const menus = ref<UserMenuItem[]>([]);
    const loaded = ref(false);
    const loading = ref(false);
    let inFlight: Promise<void> | null = null;

    function syncAuthDisplayName(detail: UserDetail | null, permissions?: string[], actions?: PermissionAction[]) {
        if (!detail) return;
        const name = detail.username || detail.nick || detail.tel || "";
        if (!name && !(detail.avatar || "").trim() && permissions === undefined && actions === undefined) return;
        const auth = useAuthStore();
        auth.setUserInfo({
            id: detail.id,
            name: name || auth.userInfo?.name || "",
            avatar: (detail.avatar || "").trim() || undefined,
            tel: detail.tel,
            email: detail.email,
            roles: auth.userInfo?.roles ?? [],
            permissions: permissions ?? auth.userInfo?.permissions ?? [],
            actions: actions ?? auth.userInfo?.actions ?? [],
        });
    }

    function applyInfo(data: CurrentUserInfoData) {
        userDetail.value = data.userDetail ?? null;
        menus.value = data.menus ?? [];
        syncAuthDisplayName(userDetail.value, data.permissions, data.actions);
        loaded.value = true;
    }

    /** 路由守卫拉菜单时复用同一次 info */
    function hydrateFromInfo(data: CurrentUserInfoData) {
        applyInfo(data);
    }

    async function fetchInfo(force = false) {
        if (loading.value && inFlight) {
            return inFlight;
        }
        if (loaded.value && !force) {
            return;
        }

        loading.value = true;
        inFlight = (async () => {
            try {
                const data = await fetchCurrentUserInfo();
                applyInfo(data);
            } catch (e) {
                // 刷新失败保留会话缓存，避免保存资料后顶栏/菜单被误清
                console.error("[currentUser]", e);
            } finally {
                loading.value = false;
                inFlight = null;
            }
        })();

        return inFlight;
    }

    function fetchIfNeeded() {
        return fetchInfo(false);
    }

    function refresh() {
        return fetchInfo(true);
    }

    function reset() {
        userDetail.value = null;
        menus.value = [];
        loaded.value = false;
        loading.value = false;
        inFlight = null;
    }

    return { userDetail, menus, loaded, loading, hydrateFromInfo, fetchIfNeeded, refresh, reset };
});
